import { Box } from "@mui/material";
import { useSelector } from "react-redux";

import {
  Autocomplete,
  Button,
  CircularProgress,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Typography,
} from "@mui/joy";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { TaskType_Entity } from "../../entities/taskType";
import {
  formOpenSelector,
  selectedTaskTypeID,
  setFormOpen,
  setSelectedTaskTypeID,
} from "../../redux/filmsReducer";
import {
  useCreateTaskTypeMutation,
  useDeleteTaskTypeMutation,
  useUpdateTaskTypeMutation,
} from "../../redux/taskType/taskTypeEndpoints";
import { useTaskType } from "../../redux/taskType/taskTypeHooks";
import { useAskQuestion } from "../askQuestion/askQuestion";
import { useJobCategories } from "../../redux/jobCategory/jobCategoryHooks";

/**
 *
 * @returns {ReactElement} FormTaskType page
 */
export function FormTaskType() {
  const taskTypeID = useSelector(selectedTaskTypeID);
  const { taskType, isLoading } = useTaskType(taskTypeID);
  const open = useSelector(formOpenSelector("taskType"));
  const [_taskType, setTaskType] = useState<Partial<TaskType_Entity>>({});

  const [createTaskType, { isLoading: isCreating }] =
    useCreateTaskTypeMutation();
  const [updateTaskType, { isLoading: isUpdating }] =
    useUpdateTaskTypeMutation();
  const [deleteTaskType] = useDeleteTaskTypeMutation();
  const { jobCategories } = useJobCategories();
  const askQuestion = useAskQuestion();

  const dispatch = useDispatch();

  useEffect(() => {
    if (taskType?.id) {
      setTaskType(taskType);
    }
  }, [taskType]);

  const onClose = () => {
    dispatch(setFormOpen({ isOpen: false, formID: "taskType" }));
    dispatch(setSelectedTaskTypeID(undefined));
  };

  const save = () => {
    if (taskTypeID && _taskType) {
      updateTaskType({ id: taskTypeID, body: _taskType });
    } else if (_taskType) {
      createTaskType(_taskType)
        .unwrap()
        .then((e) => {
          dispatch(setSelectedTaskTypeID(e.id));
        });
    }
    onClose();
  };

  const askDelete = () => {
    askQuestion("Are you sure?", ["Cancel", "Yes"], {
      subtitle: (
        <Typography>
          You are about to delete a TaskType. Please confirm this action.
        </Typography>
      ),
    }).then((i) => {
      if (i == 1) confirmDelete();
    });
  };

  const confirmDelete = async () => {
    if (taskTypeID) deleteTaskType(taskTypeID);
    onClose();
  };

  const selectedJobCategory =
    jobCategories?.find((jc) => jc.id === _taskType?.jobCategoryID) ?? null;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog layout="center" sx={{ minWidth: 520, maxWidth: "95vw" }}>
        <ModalClose onClick={onClose} />
        <Typography
          startDecorator={<i className="fa fa-hammer" />}
          level="h6"
          sx={{ fontWeight: "bold" }}
        >
          {taskTypeID ? "Edit" : "Create"} Task Type
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}
            >
              <Input
                startDecorator="Name:"
                value={_taskType.name ?? ""}
                onChange={(e) =>
                  setTaskType((v) => ({ ...v, name: e.target.value }))
                }
                type="text"
                autoFocus
              />
              <Autocomplete
                key={selectedJobCategory?.id}
                startDecorator="Job Category:"
                options={jobCategories ?? []}
                value={selectedJobCategory}
                getOptionLabel={(option) => option?.name ?? ""}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                slotProps={{
                  listbox: { sx: { zIndex: 9999 } },
                }}
                onChange={(_, value) =>
                  setTaskType((v) => ({
                    ...v,
                    jobCategoryID: value?.id ?? undefined,
                  }))
                }
              />
              <Select
                startDecorator="Unit:"
                value={_taskType.unit ?? ""}
                slotProps={{
                  listbox: { sx: { zIndex: 9999 } },
                }}
                onChange={(_, value) =>
                  setTaskType((v) => ({
                    ...v,
                    unit: value ?? undefined,
                  }))
                }
              >
                <Option value="Page">Page</Option>
                <Option value="Hour">Hour</Option>
              </Select>
              <Input
                startDecorator="Order:"
                value={_taskType.pos ?? 0}
                onChange={(e) =>
                  setTaskType((v) => ({ ...v, order: e.target.value }))
                }
                type="number"
              />
            </Box>
          </Box>
        )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {taskTypeID ? (
          <Button variant="soft" onClick={askDelete} color="danger">
            Delete
          </Button>
        ) : (
          []
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} color="neutral" variant="soft">
          Cancel
        </Button>
        <Button loading={isCreating || isUpdating} onClick={save}>
          {taskTypeID ? "Save" : "Create"}
        </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}

function formatEIN(input: string) {
  try {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, "");

    // Ensure there are exactly 9 digits
    if (digits.length !== 9) {
      throw new Error("Invalid EIN: must contain exactly 9 digits.");
    }

    // Format as XX-XXXXXXX
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  } catch (error) {
    return "";
  }
}

function formatSSN(input: string) {
  try {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, "");

    // Ensure there are exactly 9 digits
    if (digits.length !== 9) {
      throw new Error("Invalid SSN: must contain exactly 9 digits.");
    }

    // Format as XXX-XX-XXXX
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  } catch (error) {
    return "";
  }
}
